import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Inbox } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CreateApiKeyDialog } from "@/components/api-keys/CreateApiKeyDialog";
import { SecretRevealDialog } from "@/components/webhooks/SecretRevealDialog";
import { CursorPager, type Cursors } from "@/components/ui/cursor-pager";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";

type ApiKeyResponse = components["schemas"]["ApiKeyResponse"];
type CreateApiKeyResponse = components["schemas"]["CreateApiKeyResponse"];

const PAGE_SIZE = 50;

const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" });

export function ApiKeysPage() {
  const { activeClient, activeClientId } = useAuth();
  const [keys, setKeys] = useState<ApiKeyResponse[] | null>(null);
  const [cursors, setCursors] = useState<Cursors | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState<CreateApiKeyResponse | null>(null);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => {
    setCursor(null);
    setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .GET("/v1/api-keys", {
        params: {
          query: {
            Limit: PAGE_SIZE,
            ...(cursor ? { Cursor: cursor } : {}),
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { setError("Couldn't load API keys."); return; }
        setKeys(data?.data ?? []);
        setCursors(data?.meta?.cursors ?? null);
      })
      .catch(() => { if (!cancelled) setError("Network error fetching API keys."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeClientId, tick, cursor]);

  const isAdmin = activeClient?.role === "Owner" || activeClient?.role === "Admin";

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">API Keys</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            API keys let your backend call the Dolfin API. Rotate them periodically, or mint separate keys for different environments (e.g. staging vs. production). The plaintext key is shown once at creation — store it in your secrets manager immediately.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)} disabled={!isAdmin || !activeClientId}>
          <Plus className="size-3.5" /> Create key
        </Button>
      </div>

      <CreateApiKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(created) => {
          refresh();
          setRevealedKey(created);
        }}
      />

      <SecretRevealDialog
        open={revealedKey !== null}
        onOpenChange={(o) => { if (!o) setRevealedKey(null); }}
        secret={revealedKey?.rawKey ?? null}
        title={`API key created${revealedKey?.name ? ` · ${revealedKey.name}` : ""}`}
        description="This is the only time the raw key will be shown. Paste it into your backend's secrets manager now."
      />

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">{error}</div>
      ) : loading ? (
        <LoadingState />
      ) : !keys || keys.length === 0 ? (
        <EmptyState canCreate={!!isAdmin} onNew={() => setCreateOpen(true)} />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead>Name</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.map((k) => (
                <ApiKeyRow key={k.id} apiKey={k} isAdmin={!!isAdmin} onChanged={refresh} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {!error && !loading && keys && keys.length > 0 && (
        <CursorPager cursors={cursors} onNavigate={setCursor} />
      )}
    </div>
  );
}

function ApiKeyRow({
  apiKey, isAdmin, onChanged,
}: { apiKey: ApiKeyResponse; isAdmin: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const isRevoked = !!apiKey.revokedAt;

  async function revoke() {
    if (!apiKey.id) return;
    if (!confirm(`Revoke "${apiKey.name ?? apiKey.keyPrefix}"? This is irreversible — backends using this key will start failing immediately.`)) return;
    setBusy(true);
    await api.POST("/v1/api-keys/{id}/revoke", { params: { path: { id: apiKey.id } } });
    setBusy(false);
    onChanged();
  }

  return (
    <TableRow className={isRevoked ? "opacity-60" : ""}>
      <TableCell className="font-medium">{apiKey.name ?? <span className="italic text-muted-foreground">unnamed</span>}</TableCell>
      <TableCell><span className="font-mono text-xs">{apiKey.keyPrefix}…</span></TableCell>
      <TableCell>
        {isRevoked
          ? <Badge variant="outline" className="border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400">Revoked</Badge>
          : <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">Active</Badge>}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {apiKey.createdAt ? dateFmt.format(new Date(apiKey.createdAt)) : "—"}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {apiKey.expiresAt ? dateFmt.format(new Date(apiKey.expiresAt)) : "Never"}
      </TableCell>
      <TableCell className="text-right">
        {!isRevoked && isAdmin && (
          <Button variant="ghost" size="icon-sm" onClick={revoke} disabled={busy} className="text-destructive">
            {busy ? <Spinner className="size-3.5" /> : <Trash2 className="size-3.5" />}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border py-16 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" /> Loading API keys…
    </div>
  );
}

function EmptyState({ canCreate, onNew }: { canCreate: boolean; onNew: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border py-16 text-center">
      <Inbox className="size-7 text-muted-foreground" />
      <p className="text-sm font-medium">No API keys yet.</p>
      <p className="max-w-sm text-xs text-muted-foreground">
        Mint your first key for your backend to call the Dolfin API. The raw key is shown once at creation.
      </p>
      <Button size="sm" onClick={onNew} disabled={!canCreate}>
        <Plus className="size-3.5" /> Create key
      </Button>
    </div>
  );
}
